import {useInfiniteQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {useEffect, useState} from 'react';
import * as ProviderMappingsService from '../services/ProviderMappingsService';
import {notifySuccess, notifyError} from '../../../components/ServiceNotifications';
import {moduleConfig} from '../config';

// Module-scoped global filter state (shared across components)
let globalFilters: Record<string, string> = {};
const filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useProviderMappingsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return { filters, handleSetFilters, handleClearFilters };
};

export const useProviderMappings = (productId?: string, customerId?: string, priceId?: string) => {
    const queryClient = useQueryClient();
    const { filters: userFilters } = useProviderMappingsFilter();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);

    const sort = { column: 'created', direction: 'desc' as const };

    // Build contextual filter based on parent entity
    const contextFilter: Record<string, string> = {};
    if (productId) {
        contextFilter.entityType = 'Product';
        contextFilter.localEntityId = productId;
    } else if (customerId) {
        contextFilter.entityType = 'Customer';
        contextFilter.localEntityId = customerId;
    } else if (priceId) {
        contextFilter.entityType = 'Price';
        contextFilter.localEntityId = priceId;
    }

    // Merge: user filters always win over context for entityType (if the user picked one)
    // but localEntityId from context is preserved.
    const mergedFilter: Record<string, string> = {
        ...userFilters,
        ...contextFilter
    };

    const providerMappingsQuery = useInfiniteQuery({
        queryKey: ['provider-mappings', productId, customerId, priceId, keyword, mergedFilter, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }) => ProviderMappingsService.fetchRecords({
            limit,
            sort,
            nextToken: pageParam,
            filter: Object.keys(mergedFilter).length > 0 ? mergedFilter : undefined
        }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => ProviderMappingsService.deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess(`${moduleConfig.titles.singular} deleted`);
            queryClient.invalidateQueries({ queryKey: ['provider-mappings'] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const allItems = providerMappingsQuery.data?.pages?.flatMap(page => page?.items || []) || [];

    const deleteRecords = (ids?: string[]) => {
        if (ids && ids.length > 0) {
            deleteMutation.mutate(ids);
        }
    };

    return {
        items: allItems,
        loading: providerMappingsQuery.isLoading,
        nextToken: providerMappingsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            queryClient.invalidateQueries({ queryKey: ['provider-mappings'] });
        },
        fetchMoreRecords: () => {
            if (providerMappingsQuery.hasNextPage) {
                providerMappingsQuery.fetchNextPage();
            }
        },
        deleteRecords,
        handleFilterByKeyword: setKeyword
    };
};
